import React, { useState, useEffect } from 'react';
import { Row, Col, Button } from 'reactstrap';
import { MapContainer, TileLayer } from 'react-leaflet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStreetView, faSearch } from '@fortawesome/free-solid-svg-icons';
import { HeatmapLayer } from 'react-leaflet-heatmap-layer-v3/lib';
import moment from 'moment/moment';

//Components
import MainCard from '../../components/MainCard/MainCard';
import Loading from '../../components/Loading/Loading';
import TextField from '../../components/TextField/TextField';

//Api
import mapasService_api from '../../api/mapasService_api';
import modalidadeColeta_api from '../../api/modalidadeColeta_api';

//Helpers
import { showMessage } from '../../helpers/message';
import { objCampo, validarCampos } from '../../helpers/ValidadorDeCampos';

//Context
import { useAuth } from '../../context/auth';
import { MAPA_TILE_URL, MAPA_TILE_ATTRIBUTION } from '../../helpers/mapas';

function initialStateFiltros() {
    const dataInicial = new Date();
    const dataFinal = new Date();
    dataInicial.setMonth(dataInicial.getMonth() - 1);
    return {
        dataInicial: moment(dataInicial).format("YYYY-MM-DD"),
        dataFinal: moment(dataFinal).format("YYYY-MM-DD"),
        tbTipoColeta_Id: null
    }
}

export default function MapaTermico() {
    const { setupVariavel } = useAuth();
    //Estado de controle dos erros nos campos dos filtros
    const [errors, setErrors] = useState({});

    //Estado de controle dos dados dos filtros
    const [filtros, setFiltros] = useState(initialStateFiltros);

    //Estado de controle do loading na tela
    const [loading, setLoading] = useState(false);

    //Estado de controle do mapa
    const [map, setMap] = useState(null);

    //Estado de controle da visão de sateleti
    const [viewSatelite, setViewSatelite] = useState(() => false);

    //Função de troca do estado de visão de sateleti
    const toggleViewSatelite = () => setViewSatelite(() => !viewSatelite);

    //Estado que guarda os pontos das coletas programadas
    const [pontos, setPontos] = useState([]);

    //Estado de controle do ponto central do mapa
    const [location, setLocation] = useState([-23.2198396, -45.8915658]);

    //Estado de controle das opções de modalidades de coleta
    const [optionsModalidades, setOptionsModalidades] = useState([]);

    //Estado que controla a visualização dos filtros
    const [openFiltros, setOpenFiltros] = useState(false);

    //Troca de estado de visualização dos filtros
    const toggleFiltros = () => setOpenFiltros(!openFiltros);

    //Campos obrigatorios dos filtros do mapa
    const campos = [
        objCampo({ name: "dataInicial", required: true }),
        objCampo({ name: "dataFinal", required: true }),
    ]

    //Função que carrega os pontos do mapa geotermico
    const loadPontos = () => {
        const _errors = validarCampos(filtros, campos);

        setErrors(_errors[0]);

        if (_errors[1].length > 0)
            return
        if (new Date(filtros.dataInicial) > new Date(filtros.dataFinal)) {
            setErrors({ dataInicial: "" });
            return;
        }

        const _filtros = { ...filtros }
        _filtros.dataInicial = _filtros.dataInicial + " 00:00:00";
        _filtros.dataFinal = _filtros.dataFinal + " 23:59:59";
        if (!_filtros.tbTipoColeta_Id)
            _filtros.tbTipoColeta_Id = 0;

        setLoading(true);
        mapasService_api.MapaTermico(_filtros).then(response => {
            setPontos(response.data);
        }).catch(e => showMessage("Aviso!", e.message, "error", null))
            .finally(f => setLoading(false));
    }
    const loadOptionsModalidade = () => {
        setLoading(true);
        modalidadeColeta_api.GetList().then(response => {
            setOptionsModalidades(response.data.map(value => { return { value: value.id, label: value.descricao } }));
        }).catch(e => showMessage("Aviso", e.message, "error", null))
            .finally(f => setLoading(false));
    }
    //Função de onChange dos filtros do mapa
    const onChangeFiltros = (e) => {
        const { name, value } = e.target;
        setFiltros({
            ...filtros,
            [name]: value
        })
    }

    //Função de onchange do campo de tbTipoColeta_Id
    const onChangeModalidadeColeta = (e, select) => {
        const { name } = select;
        setFiltros({
            ...filtros,
            [name]: e ? e.value : e,
        });
    }

    //Carrega os pontos assim que a tela é carregada
    useEffect(() => {
        if(setupVariavel?.coordenadaPrefeitura){
            const [latitude, longitude] = setupVariavel?.coordenadaPrefeitura.split(";");
            setLocation([parseFloat(latitude), parseFloat(longitude)])
        }
        loadPontos();
        loadOptionsModalidade();
    }, []);

    return (
        <MainCard noCard title={"Mapa Térmico"} btnFiltros={toggleFiltros}>
            {loading && <Loading />}
            {loading ?
                <></>
                :
                <>
                    <div className={`filtros ${openFiltros ? "" : "close"}`} id={"filtros"}>
                        <Row>
                            <Col md="12" lg="4">
                                <TextField type={"date"} label="* Data Inicial" name="dataInicial" value={filtros.dataInicial} invalid={errors.dataInicial} onChange={onChangeFiltros} />
                            </Col>
                            <Col md="12" lg="4">
                                <TextField type={"date"} label="* Data Final" name="dataFinal" value={filtros.dataFinal} invalid={errors.dataFinal} onChange={onChangeFiltros} />
                            </Col>
                            <Col md="12" lg="4">
                                <TextField
                                    placeholder="Todos"
                                    label="Modalidade de coleta"
                                    searchable
                                    name="tbTipoColeta_Id"
                                    value={optionsModalidades.filter(value => value.value == filtros.tbTipoColeta_Id)[0]}
                                    invalid={errors.tbTipoColeta_Id}
                                    onChange={onChangeModalidadeColeta}
                                    type="select"
                                    options={optionsModalidades} />
                            </Col>
                            <Col md="12" lg="4">
                                <Button color='primary' onClick={loadPontos}><FontAwesomeIcon icon={faSearch} /> Pesquisar</Button>
                            </Col>
                        </Row>
                    </div>
                    <Row style={{ height: "65vh", backgroundColor: "#eee" }} className="mt-3 mb-5 rounded-3 overflow-hidden shadow" >
                        <Col md="12" lg="12" className=' m-0 p-0'>
                            <MapContainer
                                center={location}
                                zoom={13}
                                ref={map}
                                whenCreated={mapRef => setMap(mapRef)}
                                style={{ height: "100%", width: "100%", backgroundColor: "#333" }}
                            >

                                <Button color='light border' className='buttonSatelite' onClick={toggleViewSatelite}><FontAwesomeIcon size='1x' icon={faStreetView} /></Button>

                                <TileLayer
                                    url={MAPA_TILE_URL}
                                    attribution={MAPA_TILE_ATTRIBUTION}
                                />
                                {
                                    viewSatelite
                                    &&
                                    <TileLayer
                                        url='https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'
                                        maxZoom={20}
                                        subdomains={['mt1', 'mt2', 'mt3']}
                                    />

                                }



                                <HeatmapLayer
                                    points={pontos}
                                    longitudeExtractor={(m) => m.longitude}
                                    latitudeExtractor={(m) => m.latitude}
                                    intensityExtractor={(m) => m.quantidadeResiduos}
                                />
                            </MapContainer>
                        </Col>
                    </Row>
                </>
            }
        </MainCard>
    )
}